import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { ZAPIER_INVITE_URL } from "../../../constants";
import OptionToggler from "../../../parts/OptionToggler";
import SvgIcon from "../../../parts/SvgIcon";
import CopyField from "./components/CopyField";

const WEBHOOK_URL = "https://app.mystrengthbook.com/api/v1/zapier/perform";

const PerformPreferences = ({
  userToSendInformation,
  roleName,
  dailyPendingDigest,
  emailOnNewPending,
  inAppCommunityEnabled,
  schedulingEnabled,
  coachId,
  zapierApiKey,
  canGenerateKey,
  onFormChange,
}) => {
  const { t } = useTranslation("dashboard");
  const onOffOptions = [
    { label: t("generalPreferences.on"), value: true },
    { label: t("generalPreferences.off"), value: false },
  ];

  return (
    <form id="form-perform" method="POST" data-noreset onChange={onFormChange}>
      <input
        type="hidden"
        name="userToSendInformation"
        defaultValue={userToSendInformation}
      />
      <input type="hidden" name="roleName" defaultValue={roleName} />

      <div className="flex justify-between items-center mb-[1.2rem]">
        <h4>{t("performPreferences.zapierIntegration")}</h4>
        <div
          className={`nav-status-badge ${zapierApiKey ? "active" : "inactive"}`}
        >
          {zapierApiKey
            ? t("settingsNav.productActive")
            : t("settingsNav.productInactive")}
        </div>
      </div>
      <p className="p2 -mt-2">{t("performPreferences.zapierIntro")}</p>

      <h5 style={{ marginBottom: "0.75rem", marginTop: "1.5rem" }}>
        {t("performPreferences.zapierExampleAutomations")}
      </h5>

      <ul className="pl-4 flex flex-col gap-2 mb-4">
        <li className="list-disc">
          <p className="p2">{t("performPreferences.zapierExample1")}</p>
        </li>
        <li className="list-disc">
          <p className="p2">{t("performPreferences.zapierExample2")}</p>
        </li>
        <li className="list-disc">
          <p className="p2">{t("performPreferences.zapierExample3")}</p>
        </li>
        <li className="list-disc">
          <p className="p2">{t("performPreferences.zapierExample4")}</p>
        </li>
      </ul>

      {zapierApiKey ? (
        <>
          <CopyField label={t("performPreferences.coachId")} value={coachId} />
          <CopyField
            label={t("performPreferences.apiKey")}
            value={zapierApiKey}
          />
          <div className="flex gap-2 mt-4 zapier-actions">
            <a
              href={ZAPIER_INVITE_URL}
              role="button"
              data-button-type="primary"
              data-icon
              target="_blank"
              rel="noreferrer"
            >
              <SvgIcon src="/img/new-icons/lightning.svg" />
              {t("performPreferences.connectZapierApp")}
              <SvgIcon src="/img/new-icons/export.svg" />
            </a>
            <a
              href="https://msbapp.notion.site/Setting-Up-the-Zapier-Integration-for-PERFORM-367e68dcd77680539972e17e323c6cfe"
              role="button"
              data-button-type="tertiary"
              data-icon
              target="_blank"
              rel="noreferrer"
            >
              <SvgIcon src="/img/new-icons/book.svg" />
              {t("performPreferences.viewSetupGuide")}
              <SvgIcon src="/img/new-icons/export.svg" />
            </a>
          </div>
          <details className="mt-4">
            <summary className="p2">
              {t("performPreferences.legacyWebhookSetup")}
            </summary>
            <CopyField
              label={t("performPreferences.webhookUrl")}
              value={WEBHOOK_URL}
            />
          </details>
        </>
      ) : (
        <div className="row">
          <p style={{ opacity: 0.6 }}>
            {canGenerateKey
              ? t("performPreferences.zapierCredentialsHint")
              : t("pullPreferences.zapierCredentialsHintAdmin")}
          </p>
          {canGenerateKey && (
            <button
              type="button"
              data-button-type="primary"
              onClick={() =>
                submit("/actions/coach/generate-zapier-key", {
                  method: "POST",
                })
              }
            >
              {t("pullPreferences.generateZapierKey")}
            </button>
          )}
        </div>
      )}

      <h4 style={{ margin: "1.6rem 0 1.2rem" }}>
        {t("performPreferences.community")}
      </h4>
      <div className="push-community">
        <div className="push-community-card">
          <div className="push-community-card-info">
            <span>{t("pressPreferences.inAppCommunity")}</span>
          </div>
          <div className="push-community-card-actions">
            <OptionToggler
              name="inAppCommunityEnabled"
              options={onOffOptions}
              defaultValue={inAppCommunityEnabled}
            />
          </div>
        </div>
      </div>

      <h4 style={{ margin: "1.6rem 0 1.2rem" }}>
        {t("performPreferences.scheduling")}
      </h4>
      <div className="row">
        <p className="mobile-hidden">
          {t("performPreferences.schedulingEnabled")}
        </p>
        <div className="only-show-mobile">
          <p>{t("performPreferences.schedulingEnabled")}</p>
        </div>
        <OptionToggler
          name="schedulingEnabled"
          options={onOffOptions}
          defaultValue={schedulingEnabled}
        />
      </div>

      <h4 style={{ margin: "1.6rem 0 1.2rem" }}>
        {t("performPreferences.notifications")}
      </h4>
      <div className="row">
        <p className="mobile-hidden">
          {t("performPreferences.dailyPendingDigest")}
        </p>
        <div className="only-show-mobile">
          <p>{t("performPreferences.dailyPendingDigest")}</p>
        </div>
        <OptionToggler
          name="dailyPendingDigest"
          options={onOffOptions}
          defaultValue={dailyPendingDigest}
        />
      </div>
      <div className="row">
        <p className="mobile-hidden">
          {t("performPreferences.emailOnNewPendingMember")}
        </p>
        <div className="only-show-mobile">
          <p>{t("performPreferences.emailOnNewPendingMember")}</p>
        </div>
        <OptionToggler
          name="emailOnNewPending"
          options={onOffOptions}
          defaultValue={emailOnNewPending}
        />
      </div>
    </form>
  );
};

export default PerformPreferences;
